import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sortNewestFirst } from '../sim/relationships.js';
import type { Memory, Npc, Relationship, SleepState } from '../types.js';
import { applyPush, clearPushRefs, restRestore, solitudeRestore, tickWorld, wakeNpcs, type WorldState } from './useSim.js';
import { sleepDepth } from '../sim/engine.js';
import type { Scenario } from '../sim/scenarios.js';
import { useCamera, camCenteringOn, fitStageToWorld, zoomAt } from './useCamera.js';
import { fitAquariumWorld } from './aquarium/roomViewport.js';
import { canvasDimsFor, ROOM_PAD_TOP, ROOM_PAD_X } from './draw.js';
import { useStagePointer } from './useStagePointer.js';
import { assignCharacterBorderColors } from '../sim/bubbles.js';
import { buildCounsel, pairKey } from './social.js';
import { renderWorld, type BubbleRect, type FloaterState } from './worldRenderer.js';
import { useConversations, type ActiveConvo } from './useConversations.js';
import RadialHost from './RadialHost.js';
import { useBioRefresh } from './useBioRefresh.js';
import { stashFavorites } from './library.js';
import { type BioTab } from './BioPanel.js';
import { loadLaunchHandoff } from './CreateWorld/draftStorage.js';
import {
  AquariumChrome,
  cameraFollowCharacter,
  clearGameEntryMode,
  isAquariumEntry,
  isLocationOpen,
  useAquariumRuntime,
} from './worldRuntime/index.js';
import { applyConversationEnd } from './worldRuntime/gameConversation.js';
import GameBioPanel from './worldRuntime/GameBioPanel.js';
import LegacyGameBar from './worldRuntime/LegacyGameBar.js';
import { applyCharacterBonds, seedBidirectionalRelationships, spawnGameWorld } from './worldRuntime/gameSpawn.js';
import { failedEarly } from './worldRuntime/conversationState.js';
import { appendLiveNpc } from './worldRuntime/liveNpcActions.js';
import { useAquariumVisitorLifecycle } from './worldRuntime/useAquariumVisitorLifecycle.js';
const relKey = (a: string, b: string): string => `${a}>${b}`;
export default function GameScreen(props: {
  provider: import('../llm/client.js').LlmProvider;
  apiKeys: string[];
  model: string;
  count: number;
  roster?: Npc[];
  lingerMult: number;
  scenario: Scenario;
  onLeave: () => void;
  onReturnToCharacterCreation?: () => void;
}): JSX.Element {
  const handoff = useMemo(() => loadLaunchHandoff(), []);
  const isAquarium = isAquariumEntry() && handoff !== null;
  const aquarium = useAquariumRuntime(isAquarium ? handoff : null);
  const rosterCount = isAquarium ? handoff!.roster.length : props.count;

  const [phase, setPhase] = useState<'spawning' | 'ready'>('spawning'); const [spawned, setSpawned] = useState(0);
  const [npcs, setNpcs] = useState<Npc[]>([]);
  const [paused, setPaused] = useState(false);
  const [radial, setRadial] = useState<{ id: string; x: number; y: number } | null>(null);
  const [bioId, setBioId] = useState<string | null>(null); const [bioTab, setBioTab] = useState<BioTab>('personality');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [spawnOffline, setSpawnOffline] = useState(false);
  const worldRef = useRef<WorldState | null>(null); const npcsRef = useRef<Npc[]>([]);
  const relsRef = useRef(new Map<string, Relationship>());
  const memsRef = useRef<Memory[]>([]); const longRef = useRef(new Map<string, string>());
  const chatRecencyRef = useRef(new Map<string, number>());
  const walkRef = useRef(new Map<string, number>());
  const activeRef = useRef<ActiveConvo[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [, force] = useState(0);
  const camera = useCamera();
  const camRef = useRef(camera.cam);
  camRef.current = camera.cam;
  const focusIdRef = useRef<string | null>(null);
  focusIdRef.current = isAquarium ? (aquarium.view?.focusedCharacterId ?? null) : null;
  const floatersRef = useRef<FloaterState[]>([]);
  const bubbleRectsRef = useRef<BubbleRect[]>([]);
  const borderColorsRef = useRef<Record<string, string>>({});
  npcsRef.current = npcs;

  const locationOpen = !isAquarium || (aquarium.view ? isLocationOpen(aquarium.view.operatingStatus) : true);

  const ctx = useMemo(
    () => ({
      npcById: (id: string): Npc => npcsRef.current.find((n) => n.id === id)!,
      relOf: (a: string, b: string): Relationship | null => relsRef.current.get(relKey(a, b)) ?? null,
      memsOf: (a: string, b: string): Memory[] =>
        sortNewestFirst(memsRef.current.filter((m) => m.npcId === a && m.targetId === b)),
      longOf: (a: string, b: string): string => longRef.current.get(relKey(a, b)) ?? '',
      worldHandoff: handoff,
      sleepOf: (id: string): SleepState => {
        const w = worldRef.current;
        const r = w?.runtime.find((x) => x.npcId === id);
        return r && w ? sleepDepth(w.now, r) : 'awake';
      },
    }),
    [],
  );
  async function handleEnded(convo: ActiveConvo): Promise<void> {
    const w = worldRef.current;
    if (!w) return;
    if (!failedEarly(convo)) chatRecencyRef.current.set(pairKey(convo.aId, convo.bId), w.now);
    await applyConversationEnd({
      convo,
      world: w,
      npcById: ctx.npcById,
      rels: relsRef.current,
      mems: memsRef.current,
      long: longRef.current,
      floaters: floatersRef.current,
      onNpcEnergy: setNpcs,
      onForceRender: () => force((v) => v + 1),
      aquarium:
        isAquarium && handoff && aquarium.runtimeState && aquarium.view
          ? {
              handoff,
              runtimeState: aquarium.runtimeState,
              activeDevices: aquarium.view.activeDevices,
              setRuntimeState: aquarium.setRuntimeState,
            }
          : undefined,
    });
  }
  const convos = useConversations(ctx, {
    onPaired: (aId, bId) => { const w = worldRef.current; if (w) worldRef.current = wakeNpcs(w, [aId, bId]); },
    onEnded: (c) => void handleEnded(c),
    worldNowIso: () => aquarium.runtimeState?.clock.inWorldIso ?? handoff?.snapshot.clock.inWorldIso,
  }, props.lingerMult, props.scenario);
  useEffect(() => {
    activeRef.current = convos.active;
  }, [convos.active]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await spawnGameWorld({
        isAquarium,
        handoff,
        provider: props.provider,
        apiKeys: props.apiKeys,
        model: props.model,
        count: props.count,
        nowIso: isAquarium ? aquarium.runtimeState?.clock.inWorldIso ?? handoff?.snapshot.clock.inWorldIso : undefined,
        roster: props.roster,
        onProgress: (n) => {
          if (!cancelled) setSpawned(n);
        },
      });
      if (cancelled) return;
      setSpawnOffline(result.spawnOffline);
      npcsRef.current = result.npcs;
      borderColorsRef.current = assignCharacterBorderColors(result.npcs.map((n) => n.id));
      setNpcs(result.npcs);
      worldRef.current = result.world;
      seedBidirectionalRelationships(relsRef.current, result.npcs);
      if (handoff?.snapshot.characterBonds?.length && handoff.characters.length) {
        applyCharacterBonds(
          relsRef.current,
          result.npcs,
          handoff.characters,
          handoff.snapshot.characterBonds,
        );
      }
      setPhase('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useAquariumVisitorLifecycle({
    enabled: isAquarium,
    ready: phase === 'ready',
    handoff,
    runtimeState: aquarium.runtimeState,
    npcsRef,
    worldRef,
    relationshipsRef: relsRef,
    setNpcs,
    dropConversationFor: convos.dropFor,
  });

  const fitRoom = useCallback((): void => {
    const world = worldRef.current;
    const stage = stageRef.current;
    if (!world || !stage || isAquarium) return;
    fitStageToWorld(camera.fit, stage, canvasDimsFor(world.size).w, canvasDimsFor(world.size).h);
  }, [camera.fit, isAquarium]);

  const applyFocusZoom = useCallback((id: string): void => {
    const w = worldRef.current;
    const stage = stageRef.current;
    if (!w || !stage) return;
    const r = w.runtime.find((x) => x.npcId === id);
    if (!r) return;
    const cw = w.width ?? w.size;
    const ch = w.height ?? w.size;
    const rect = stage.getBoundingClientRect();
    camera.setCam(cameraFollowCharacter({
      cam: camera.cam,
      stageW: rect.width,
      stageH: rect.height,
      worldX: r.x,
      worldY: r.y,
      canvasW: cw,
      canvasH: ch,
      minScale: camera.minScale.current,
    }));
  }, [camera]);

  const focusCharacter = useCallback((id: string): void => {
    aquarium.setFocusedCharacterId(id);
    applyFocusZoom(id);
  }, [aquarium, applyFocusZoom]);

  const stopFocus = useCallback((): void => {
    focusIdRef.current = null;
    aquarium.setFocusedCharacterId(null);
    camera.setCam({ scale: 1, ox: 0, oy: 0 });
  }, [aquarium, camera]);

  useEffect(() => {
    if (phase !== 'ready' || isAquarium) return;
    fitRoom();
  }, [phase, fitRoom, isAquarium]);

  useEffect(() => {
    if (phase !== 'ready') return;
    if (!isAquarium) {
      const onResize = (): void => {
        if (worldRef.current) fitRoom();
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    camera.minScale.current = 1;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width < 2 || height < 2) return;
      const w = worldRef.current;
      if (!w) return;
      worldRef.current = fitAquariumWorld(w, width, height);
      const focusId = focusIdRef.current;
      if (focusId) {
        const r = worldRef.current.runtime.find((x) => x.npcId === focusId);
        if (r) {
          const cw = worldRef.current.width ?? worldRef.current.size;
          const ch = worldRef.current.height ?? worldRef.current.size;
          camera.setCam(camCenteringOn(
            camRef.current, width, height, r.x, r.y, cw, ch, 0, 0,
          ));
        }
      } else {
        camera.setCam({ scale: 1, ox: 0, oy: 0 });
      }
    });
    ro.observe(stage);
    return () => ro.disconnect();
  }, [phase, isAquarium, camera]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || phase !== 'ready') return;
    const onWheel = (e: WheelEvent): void => {
      if ((e.target as HTMLElement).closest('.bio,.radial,.aquarium-panel')) return;
      e.preventDefault();
      const r = stage.getBoundingClientRect();
      camera.setCam(zoomAt(camRef.current, e.clientX - r.left, e.clientY - r.top, e.deltaY, camera.minScale.current, 3));
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [phase, camera]);

  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const convosRef = useRef(convos);
  convosRef.current = convos;

  const counsel = useMemo(
    () =>
      buildCounsel({
        relOf: (selfId, otherId) => {
          const r = relsRef.current.get(relKey(selfId, otherId));
          return r ? { affinity: r.affinity, attraction: r.attraction } : null;
        },
        msSinceChat: (selfId, otherId) => {
          const w = worldRef.current;
          const t = chatRecencyRef.current.get(pairKey(selfId, otherId));
          if (t == null || !w) return null;
          return w.now - t;
        },
      }),
    [],
  );

  useEffect(() => {
    if (phase !== 'ready') return;
    let raf = 0;
    let last = performance.now();
    const frame = (t: number): void => {
      raf = requestAnimationFrame(frame);
      const dtMs = Math.min(100, t - last);
      last = t;
      const w = worldRef.current;
      if (!w || pausedRef.current || dtMs <= 0) {
        renderFrame();
        return;
      }
      if (locationOpen) {
        const energyOf = (id: string): number => npcsRef.current.find((n) => n.id === id)?.state.energy ?? 5;
        const { next, events } = tickWorld(w, dtMs, energyOf, counsel);
        worldRef.current = next;
        for (const [a, b] of events.newPairs) convosRef.current.request(a, b);
        const restore = (id: string, v: number): void => {
          npcsRef.current = npcsRef.current.map((n) =>
            n.id === id ? { ...n, state: { ...n.state, energy: v } } : n,
          );
        };
        solitudeRestore(next, energyOf, restore);
        restRestore(next, energyOf, restore);
        convosRef.current.tick(dtMs, next.now);
        for (const r of next.runtime) {
          if (r.moving && !r.busyWith) walkRef.current.set(r.npcId, (walkRef.current.get(r.npcId) ?? 0) + dtMs / 65);
        }
      }
      if (isAquarium && focusIdRef.current) {
        const stage = stageRef.current;
        const id = focusIdRef.current;
        const focused = worldRef.current?.runtime.find((x) => x.npcId === id);
        if (stage && worldRef.current && focused) {
          const rect = stage.getBoundingClientRect();
          const cw = worldRef.current.width ?? worldRef.current.size;
          const ch = worldRef.current.height ?? worldRef.current.size;
          const next = camCenteringOn(
            camRef.current, rect.width, rect.height, focused.x, focused.y, cw, ch, 0, 0,
          );
          if (next.ox !== camRef.current.ox || next.oy !== camRef.current.oy) {
            camera.setCam(next);
          }
        }
      }
      renderFrame();
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [phase, locationOpen, counsel, isAquarium, camera]);

  function renderFrame(): void {
    const canvas = canvasRef.current;
    const w = worldRef.current;
    if (!canvas || !w) return;
    const out = renderWorld({
      canvas, w, npcs: npcsRef.current, active: activeRef.current,
      walk: walkRef.current, floaters: floatersRef.current, hoverConvoId: hoverConvoRef.current,
      borderColors: borderColorsRef.current,
    });
    floatersRef.current = out.floaters;
    bubbleRectsRef.current = out.rects;
  }

  const lastClient = useRef({ x: 0, y: 0 });
  const hoverConvoRef = useRef<string | null>(null);
  useBioRefresh(bioId, npcsRef, setNpcs);

  useEffect(() => {
    if (!radial) return;
    const t = setInterval(() => {
      const world = worldRef.current;
      const r = world?.runtime.find((x) => x.npcId === radial.id);
      const canvas = canvasRef.current;
      if (!r || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const w = worldRef.current;
      const padX = w?.width != null ? 0 : ROOM_PAD_X;
      const padTop = w?.height != null ? 0 : ROOM_PAD_TOP;
      const scale = rect.width / canvas.width;
      const sx = rect.left + (r.x + padX) * scale;
      const sy = rect.top + (r.y + padTop) * scale;
      const half = 96;
      setRadial({
        id: radial.id,
        x: Math.max(half + 6, Math.min(sx, window.innerWidth - half - 6)),
        y: Math.max(half + 6, Math.min(sy, window.innerHeight - half - 6)),
      });
    }, 250);
    return () => clearInterval(t);
  }, [radial]);

  const stageInput = useStagePointer({
    canvasRef, worldRef, activeRef, bubbleRectsRef, hoverConvoRef, lastClient,
    camera, bioId, setBioId, setRadial, setWorld: (w) => { worldRef.current = w; },
    centerOnSelect: aquarium.view ? aquarium.view.isNarrowViewport === true : (typeof window !== 'undefined' && window.innerWidth < 720),
    skipBubble: (id) => convos.skipLine(id),
  });

  function deleteNpc(id: string): void {
    setNpcs((list) => list.filter((n) => n.id !== id));
    npcsRef.current = npcsRef.current.filter((n) => n.id !== id);
    const w = worldRef.current;
    if (w) worldRef.current = clearPushRefs(w, id);
    convos.dropFor(id);
    setRadial(null);
    setBioId(null);
  }

  function handleLeave(): void {
    if (isAquarium) aquarium.persistVisit();
    else stashFavorites(npcsRef.current, memsRef.current, longRef.current);
    clearGameEntryMode();
    props.onLeave();
  }

  if (phase === 'spawning') {
    return (
      <div className="screen">
        <h1>{isAquarium ? 'Opening aquarium…' : 'Waking minds…'}</h1>
        <div className="subtitle">{spawned}/{rosterCount} {isAquarium ? 'characters' : 'NPCs'} ready</div>
      </div>
    );
  }

  const w = worldRef.current;
  const bioNpc = bioId ? npcs.find((n) => n.id === bioId) ?? null : null;
  const dims = w
    ? (w.width != null && w.height != null ? { w: w.width, h: w.height } : canvasDimsFor(w.size))
    : { w: 800, h: 600 };
  const bioRt = bioNpc ? w?.runtime.find((r) => r.npcId === bioNpc.id) : undefined;

  return (
    <div className={`game-wrap${isAquarium ? ' aquarium-mode' : ''}${aquarium.view?.isNarrowViewport ? ' aquarium-narrow' : ''}`}>
      {isAquarium && aquarium.view ? (
        <>
          <div className="game-bar aquarium-game-bar">
            <span className="spacer" />
            <button className="btn-ghost" onClick={() => setConfirmLeave(true)}>Leave</button>
          </div>
          <AquariumChrome
            view={aquarium.view}
            npcs={npcs}
            runtimeState={aquarium.runtimeState}
            setRuntimeState={aquarium.setRuntimeState}
            onFocusCharacter={focusCharacter}
            onStopFocus={stopFocus}
            onDismissMissed={aquarium.dismissMissedPrompt}
            onBackToMenu={handleLeave}
            onAddCharacter={(npc) => appendLiveNpc(npc, npcsRef, worldRef, relsRef.current, setNpcs)}
            onRemoveCharacter={deleteNpc}
            onReturnToCharacterCreation={() => {
              aquarium.persistVisit();
              props.onReturnToCharacterCreation?.();
            }}
          />
        </>
      ) : (
        <LegacyGameBar
          scenario={props.scenario} npcs={npcs} activeCount={convos.active.length}
          pendingCount={convos.pending} generatingCount={convos.generating}
          spawnOffline={spawnOffline} llmDown={convos.llmDown} paused={paused}
          onFit={fitRoom} onTogglePause={() => setPaused((p) => !p)} onLeave={() => setConfirmLeave(true)}
        />
      )}
      <div
        ref={stageRef}
        className={camera.dragging ? 'stage grabbing' : 'stage'}
        onMouseDown={stageInput.onStageMouseDown}
        onMouseMove={stageInput.onStageMouseMove}
        onMouseUp={stageInput.onStageMouseUp}
        onMouseLeave={stageInput.onStageMouseLeave}
      >
        <canvas
          ref={canvasRef}
          className="room"
          style={w ? { width: dims.w * camera.cam.scale, height: dims.h * camera.cam.scale, transform: `translate(${camera.cam.ox}px, ${camera.cam.oy}px)` } : { width: 800 }}
        />
      </div>
      {radial && (
        <RadialHost
          radialId={radial.id} x={radial.x} y={radial.y} npcs={npcs}
          runtime={worldRef.current?.runtime ?? []} now={worldRef.current?.now ?? 0}
          onBio={(id) => {
            setBioId(id);
            setRadial(null);
            if (isAquarium && aquarium.view?.isNarrowViewport) focusCharacter(id);
          }}
          onPush={(fromId, targetId) => { const world = worldRef.current; if (world) worldRef.current = applyPush(world, fromId, targetId); setRadial(null); }}
          onSave={(id) => { setNpcs((list) => list.map((n) => (n.id === id ? { ...n, savedFavorite: !n.savedFavorite } : n))); npcsRef.current = npcsRef.current.map((n) => (n.id === id ? { ...n, savedFavorite: !n.savedFavorite } : n)); setRadial(null); }}
          onDelete={(id) => {
            const npc = npcsRef.current.find((n) => n.id === id);
            const name = npc ? `${npc.firstName} ${npc.lastName}`.trim() : 'this character';
            if (!window.confirm(`Delete "${name}" from this world? This cannot be undone.`)) return;
            deleteNpc(id);
          }}
          onClose={() => setRadial(null)}
        />
      )}
      {bioNpc && w && (
        <GameBioPanel
          bioNpc={bioNpc} bioTab={bioTab} onTab={setBioTab}
          sleep={bioRt && w ? sleepDepth(w.now, bioRt) : 'awake'}
          bedSecs={bioRt?.restingBed ? Math.max(0, Math.floor((w.now - bioRt.bedSince) / 1000)) : 0}
          wakeAt={bioRt?.wakeAt ?? 0}
          npcs={npcs} w={w} rels={relsRef.current}
          memsOf={ctx.memsOf} longOf={ctx.longOf} chatRecency={chatRecencyRef.current}
          onClose={() => setBioId(null)}
        />
      )}
      {confirmLeave && (
        <div className="modal-back">
          <div className="modal">
            <h2>Leave this world?</h2>
            <p>{isAquarium ? 'Your world history is saved for when you return.' : 'All NPCs here will be wiped (saved ★ favorites are kept for later).'}</p>
            <div className="menu-row">
              <button className="btn-ghost" onClick={() => setConfirmLeave(false)}>Stay</button>
              <button className="btn-primary" onClick={handleLeave}>Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
